import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Modal Navigation Component
 * Provides next/previous navigation for traversing through items in a modal
 * 
 * @param {Object} props
 * @param {number} props.currentIndex - Current item index
 * @param {number} props.totalCount - Total number of items
 * @param {Function} props.onPrevious - Callback when clicking previous
 * @param {Function} props.onNext - Callback when clicking next
 * @param {string} props.itemName - Name of the item type (e.g., "Job", "Proposal")
 */
export function ModalNavigation({ currentIndex, totalCount, onPrevious, onNext, itemName = "Item" }) {
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < totalCount - 1;

  return (
    <div className="flex items-center gap-3">
      {/* Previous Button */}
      <button
        onClick={onPrevious}
        disabled={!hasPrevious}
        className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
          hasPrevious
            ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            : 'bg-gray-50 text-gray-400 cursor-not-allowed'
        }`}
        title={hasPrevious ? `Previous ${itemName}` : 'No previous items'}
      >
        <ChevronLeft className="h-4 w-4" />
        <span className="hidden sm:inline">Previous</span>
      </button>

      {/* Counter */}
      <span className="text-sm text-gray-500 min-w-[80px] text-center">
        {currentIndex + 1} of {totalCount}
      </span>

      {/* Next Button */}
      <button
        onClick={onNext}
        disabled={!hasNext}
        className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
          hasNext
            ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            : 'bg-gray-50 text-gray-400 cursor-not-allowed'
        }`}
        title={hasNext ? `Next ${itemName}` : 'No more items'}
      >
        <span className="hidden sm:inline">Next</span>
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}

export default ModalNavigation;
